import { createSelector } from 'reselect';
import format from 'date-fns/format';
import getDay from 'date-fns/get_day';
import isWithinRange from 'date-fns/is_within_range';
import { boolToInt } from 'utils/object';
import parse from 'date-fns/parse';

// Selectors
import { getTokenId } from 'store/reducers/tokenId/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';

import { getRecallServices } from 'store/reducers/recall/selectors';
import { getDeferredServicesIds } from 'store/reducers/deferred/selectors';
import { getContactInfo } from './contactInfo/selectors';
import { isPartiallySignedIn } from 'store/reducers/user/social/selectors';
import { isLoggedIn } from 'store/reducers/user/selectors';
import { getQueryParam } from 'store/reducers/queryParams/selectors';
import {
  getPromotionType,
  getPromotionDiscountAmt,
  getPromotionServices as getPromotionServicesFromPromoData,
  getPromoDaysOfWeekArray,
  getPromotionStartDate,
  getPromotionEndDate,
  getPromoStartTime,
  getPromoEndTime,
  getActivePromotion,
  hasPromotion
} from 'store/reducers/promotions/selectors';
import { getIsQuoteAppt } from 'store/reducers/quoteAppt/selectors';

// Utils
import { isEmpty } from 'utils/string';
import { isTrue, isObjEmpty } from 'utils/varValidation';
import {
  applyPromotionToService,
  applyPromotionToPackage
} from 'utils/promotions';
import { clearTime, formatIOSDate } from 'utils/date';
import { getMetaTagContentByName } from 'utils/DOM';

// Constants
import { APPOINTMENT_STATUS } from 'constants/appointmentStatus';
import { APP_NAMES } from 'constants/appName';

const { UNKNOWN, NOT_ARRIVED, REVIEWED, PAUSED } = APPOINTMENT_STATUS;

export const getAppointment = state => state.appointment;

export const getPackages = state => state.packages.servicePoints;

export const getTransportation = state =>
  state.appointment.transportationOption;

export const getAjaxCallStack = state => state.ajaxCall.stack;

export const getReservationId = createSelector(
  getAppointment,
  ({ reservationId }) => reservationId
);

export const getParticipantComments = createSelector(
  getAppointment,
  ({ participantComments }) => participantComments
);

export const getCallBack = createSelector(
  getAppointment,
  ({ callBack }) => callBack
);

export const getAdvisorId = createSelector(
  getAppointment,
  ({ advisorId }) => advisorId
);

export const getTransportationOption = createSelector(
  getAppointment,
  ({ transportationOption }) => transportationOption
);

export const getRedirectedToDropoff = createSelector(
  getAppointment,
  ({ redirectedToDropoff }) => redirectedToDropoff
);

export const getTransportationLabel = createSelector(
  getAppointment,
  ({ transportationDisplayText }) => transportationDisplayText
);

export const getTranspOptionInfo = createSelector(
  getAppointment,
  ({ transpOptionInfo }) => transpOptionInfo
);

export const getAdvisorName = createSelector(
  getAppointment,
  ({ advisorName }) => advisorName
);

export const getVehicle = createSelector(
  getAppointment,
  ({ vehicle }) => vehicle
);

export const isCarryOver = createSelector(
  getAppointment,
  ({ isCarryOver }) => isCarryOver
);

export const getPreferredAdvisorId = createSelector(
  getVehicle,
  ({ preferredAdvisorId }) => preferredAdvisorId
);

export const makeSelectedSelector = createSelector(
  getVehicle,
  ({ make }) => make
);

export const getModel = createSelector(getVehicle, ({ model }) => model);

export const getTrim = createSelector(getVehicle, ({ trim }) => trim);

export const getTransmission = createSelector(
  getVehicle,
  ({ transmission, transmissionType }) => transmission || transmissionType
);

export const getDriveType = createSelector(
  getVehicle,
  ({ driveType }) => driveType
);

export const getEngineType = createSelector(
  getVehicle,
  ({ engineType }) => engineType
);

export const getEngineSize = createSelector(
  getVehicle,
  ({ engineSize }) => engineSize
);

export const getVin = createSelector(getVehicle, ({ vin }) => vin);

export const getMileage = createSelector(getVehicle, ({ mileage }) => mileage);

export const getYear = createSelector(getVehicle, ({ year }) => year);

export const getVehicleProperty = createSelector(
  getVehicle,
  vehicle => property => vehicle[property]
);

export const getMetaVehicleId = createSelector(
  getVehicle,
  ({ metaVehicleId }) => metaVehicleId
);

export const getVehicleId = createSelector(
  getVehicle,
  ({ vehicleId }) => vehicleId
);

export const getVehicleRegDate = createSelector(
  getVehicle,
  ({ regDate }) => regDate
);

export const getRegDate = createSelector(
  getVehicleRegDate,
  getYear,
  (regDate, year) => {
    return regDate || format(new Date(year, 0, 1), 'YYYY-MM-DD');
  }
);

export const getStartTime = createSelector(getAppointment, appointment => {
  if (appointment) {
    return appointment.startTime;
  } else {
    return null;
  }
});

export const getAppointmentTime = createSelector(getStartTime, startTime => {
  if (startTime) {
    return format(startTime, 'HH:mm');
  } else {
    return null;
  }
});

export const getAppointmentDate = createSelector(getStartTime, startTime => {
  if (startTime) {
    return format(startTime, 'YYYY-MM-DD');
  } else {
    return null;
  }
});

export const getTeamIds = createSelector(getAppointment, appointment => {
  if (appointment) {
    return appointment.teamIds;
  } else {
    return null;
  }
});

export const getAttendanceStatus = createSelector(
  getAppointment,
  appointment => {
    if (appointment) {
      return appointment.attendanceStatus;
    } else {
      return null;
    }
  }
);

export const isApptInPromoInterval = createSelector(
  getStartTime,
  getPromoDaysOfWeekArray,
  getPromotionStartDate,
  getPromotionEndDate,
  getPromoStartTime,
  getPromoEndTime,
  (
    apptTime,
    promoDays,
    promoStartDate,
    promoEndDate,
    promoStartTime,
    promoEndTime
  ) => {
    if (!apptTime) {
      return true;
    } else {
      if (!+promoDays[getDay(apptTime)]) {
        return false;
      } else {
        const maxDate = new Date(`${promoEndDate}T${promoEndTime}`);
        const minDate = new Date(`${promoStartDate}T${promoStartTime}`);
        if (isWithinRange(apptTime, minDate, maxDate)) {
          const date = format(apptTime, 'YYYY-MM-DD');
          const maxDateTime = formatIOSDate(`${date}T${promoEndTime}`);
          const minDateTime = formatIOSDate(`${date}T${promoStartTime}`);
          return isWithinRange(apptTime, minDateTime, maxDateTime);
        } else {
          return false;
        }
      }
    }
  }
);

export const isPromotionInRange = createSelector(
  hasPromotion,
  isApptInPromoInterval,
  (hasPromotion, isApptInPromoInterval) => hasPromotion && isApptInPromoInterval
);

export const getPromotionServices = createSelector(
  getPromotionServicesFromPromoData,
  isApptInPromoInterval,
  (promoServices, isApptInPromo) => (isApptInPromo ? promoServices : null)
);

export const getServices = createSelector(
  getAppointment,
  getPromotionServices,
  (appointment, promotionServices) => {
    if (appointment) {
      if (promotionServices) {
        return applyPromotionToService(promotionServices, appointment.services);
      } else {
        return appointment.services;
      }
    } else {
      return null;
    }
  }
);

export const getParamServices = createSelector(
  getServices,
  getRecallServices,
  (services, recalls) => {
    const result = [];
    if (services) {
      services.forEach(service => {
        const { serviceKind, selected, ...selectedSevice } = service;
        if (serviceKind === 'autorecall') {
          const recall = recalls.find(recall => recall.id === service.id);
          result.push({
            id: recall.parentServiceId || recall.definitionId,
            shopDuration: recall.duration,
            notes: recall.campaignId
              ? recall.manufacturerCampaignId + ' -'
              : recall.serviceComments
          });
        } else {
          result.push(selectedSevice);
        }
      });
    }
    return result;
  }
);

export const getParamRecallIds = createSelector(
  getServices,
  services =>
    services
      .map(service => {
        if (service.serviceKind === 'autorecall') {
          return service.id;
        } else {
          return null;
        }
      })
      .filter(id => Boolean(id)) || []
);

export const getParamRecalls = createSelector(
  getParamRecallIds,
  getRecallServices,
  (selectedRecallIds, recalls) =>
    recalls.filter(recall => selectedRecallIds.indexOf(recall.id) >= 0)
);

export const getServicePoint = createSelector(
  getAppointment,
  ({ servicePoint }) => servicePoint
);

export const getLeadTimeInDays = createSelector(
  getParamRecalls,
  recallSelected =>
    recallSelected
      .map(recall => recall.leadTimeInDays || 0)
      .sort((a, b) => b - a)[0]
);

export const getApptMenuType = createSelector(
  getServicePoint,
  getPromotionServices,
  isApptInPromoInterval,
  (servicePoint, promotionServices, isApptInPromo) => {
    if (servicePoint) {
      if (promotionServices && isApptInPromo) {
        return applyPromotionToPackage(
          promotionServices,
          servicePoint.menuType
        );
      } else {
        return {
          ...servicePoint.menuType,
          ...(servicePoint.quoteServiceId
            ? {
                quoteServiceId: servicePoint.quoteServiceId,
                selected: !!servicePoint.selected
              }
            : {}),
          discountedPrice: servicePoint.menuType.totalPrice
        };
      }
    } else {
      return {};
    }
  }
);

export const getServicePointParams = createSelector(
  getServicePoint,
  getPackages,
  (servicePoint, packages) => {
    if (servicePoint && packages.length > 0) {
      const {
        menuType: {
          totalPriceType,
          id,
          laborPrice,
          partsPrice,
          salesTax,
          shopCharge,
          rank,
          promotion,
          discountedPrice,
          services: packagesServices,
          shopDuration,
          selectable,
          ...menuTypeProps
        },
        selected,
        ...servicePointProps
      } = servicePoint;
      const calculateShopDuration = (services, menuShopDuration) => {
        let sumShopDuration = 0;
        const selectableMenu = isSelectableMenu(
          packages,
          servicePoint.mileage,
          servicePoint.menuType.name,
          servicePoint.menuType.description
        );
        if (selectableMenu) {
          services.forEach(({ shopDuration }) => {
            if (shopDuration) {
              sumShopDuration += +shopDuration;
            }
          });
          return sumShopDuration;
        }
        return menuShopDuration;
      };
      const isSelectableMenu = (
        packages,
        selectedMileage,
        selectedMenuName,
        selectedMenuDescription
      ) => {
        const selectedServicePoint = packages.find(
          servicePoint => servicePoint.mileage === selectedMileage
        );
        const selectedMenuType =
          selectedServicePoint &&
          selectedServicePoint.menuTypes.find(
            menuType => menuType.name === selectedMenuName
          );
        return (
          selectedMenuType &&
          selectedMenuType.services.find(service => service.selectable)
        );
      };
      return {
        menuType: {
          services: packagesServices.map(
            ({
              serviceCategory,
              order,
              type,
              description,
              dmsOpcode,
              discountedPrice,
              promotion,
              ...services
            }) => boolToInt({ ...services, dmsOpcode: dmsOpcode || '' })
          ),
          shopDuration: calculateShopDuration(packagesServices, shopDuration),
          ...boolToInt(menuTypeProps)
        },
        ...servicePointProps
      };
    }
    return {};
  }
);

export const getPackageServices = createSelector(
  getApptMenuType,
  menuType => (menuType ? menuType.services : [])
);

export const getDeclinedServices = createSelector(
  getAppointment,
  getPromotionServices,
  ({ declinedServices }, promotionServices) => {
    if (promotionServices) {
      return applyPromotionToService(promotionServices, declinedServices);
    } else {
      return declinedServices;
    }
  }
);

export const getSelectedDeclinedServicesIds = createSelector(
  getDeclinedServices,
  declinedServices => declinedServices.map(service => service.id)
);

export const getServicesIds = createSelector(getServices, services =>
  services.map(service => service.id)
);

export const getSelectedDeferredServicesIds = createSelector(
  getServicesIds,
  getDeferredServicesIds,
  (services, deferredServicesIds) =>
    services.filter(id => deferredServicesIds.indexOf(id) > -1)
);

export const getAppointmentParams = createSelector(
  getReservationId,
  getTokenId,
  getParamServices,
  getParamRecalls,
  getParamRecallIds,
  makeSelectedSelector,
  getModel,
  getLocaleCode,
  getMetaVehicleId,
  getServicePointParams,
  getDeclinedServices,
  (
    reservationId,
    tokenId,
    services,
    recalls,
    recallIds,
    make,
    model,
    locale,
    metaVehicleId,
    servicePointParams,
    declinedServices
  ) => {
    const isSelfCheckIn =
      getMetaTagContentByName('xtime:appName') === APP_NAMES.SELF_CHECK_IN;
    let autoRecallServices;
    if (isSelfCheckIn) {
      autoRecallServices = JSON.stringify(
        recalls.map(
          ({
            name,
            serviceComments: notes,
            parentServiceId,
            definitionId,
            description,
            duration,
            id
          }) => {
            return {
              id,
              notes,
              parentServiceId: definitionId || parentServiceId,
              name,
              description,
              duration
            };
          }
        )
      );
    } else {
      autoRecallServices = JSON.stringify(
        recalls.map(
          ({
            campaignCategory,
            campaignDate,
            campaignId: notes,
            customerVisible,
            isActive,
            leadTimeInDays,
            status,
            statusDate,
            serviceKind,
            manufacturerCampaignId,
            ...recall
          }) => {
            // TODO: refactor this, for some reason this is not listening line 264
            // "notes: recall.campaignId + ' -'"
            return {
              notes: notes ? notes + ' -' : manufacturerCampaignId + ' -',
              ...recall
            };
          }
        )
      );
    }

    const formattedServices = services.map(
      ({ discountedPrice, promotion, priceCaption, ...service }) => ({
        ...service
      })
    );
    const formattedDeclinedServices = declinedServices.map(
      ({
        discountedPrice,
        promotion,
        priceCaption,
        selected,
        ...declinedService
      }) => ({
        ...declinedService
      })
    );

    return {
      ...(reservationId && { reservationId }),
      tokenId,
      services: JSON.stringify(formattedServices),
      declinedServices: JSON.stringify(formattedDeclinedServices),
      servicePoint: JSON.stringify(servicePointParams),
      autoRecallIds: JSON.stringify(recallIds),
      autoRecallServices,
      make,
      model,
      locale,
      metaVehicleId
    };
  }
);

export const getIndividualTotalPrice = createSelector(
  getServices,
  getDeclinedServices,
  isApptInPromoInterval,
  (services, declinedServices, isApptInPromo) => {
    let result = { price: 0, discountedPrice: 0 };
    if (services.length + declinedServices.length > 0) {
      result = services.concat(declinedServices).reduce(
        (a, b) => ({
          price: a.price + (b.price || 0),
          discountedPrice:
            a.discountedPrice + (b.discountedPrice || b.price || 0)
        }),
        result
      );
    }
    // If the selected appointment time slot is not in promotion,
    // the discounted price will be the original price
    if (!isApptInPromo) {
      result.discountedPrice = result.price;
    }
    return result;
  }
);

export const getTotalPrice = createSelector(
  getIndividualTotalPrice,
  getApptMenuType,
  getPromotionType,
  getPromotionDiscountAmt,
  isApptInPromoInterval,
  (
    individualTotalPrice,
    menuType,
    promoType,
    promoDiscountAmt,
    isApptInPromo
  ) => {
    let price = individualTotalPrice.price;
    if (!isObjEmpty(menuType)) {
      price = individualTotalPrice.price + menuType.totalPrice;
    }
    if (isApptInPromo) {
      if (promoType === 'currencyApptDiscount') {
        return {
          price,
          discountedPrice: Math.max(0, price - promoDiscountAmt)
        };
      }
      if (promoType === 'percentApptDiscount') {
        return {
          price,
          discountedPrice: (price * (100 - promoDiscountAmt)) / 100
        };
      }
      if (promoType) {
        let discountedPrice = individualTotalPrice.discountedPrice;
        if (!isObjEmpty(menuType)) {
          discountedPrice =
            individualTotalPrice.discountedPrice +
            (menuType.discountedPrice || menuType.totalPrice);
        }
        return {
          price,
          discountedPrice
        };
      }
    }
    return {
      price,
      discountedPrice: price
    };
  }
);

export const getServicesCount = createSelector(
  getServices,
  getServicePoint,
  getDeclinedServices,
  (services, servicePackage, declinedServices) =>
    services.length + declinedServices.length + +Boolean(servicePackage)
);

export const getIndividualServicesCount = createSelector(
  getServices,
  getServicePoint,
  getDeclinedServices,
  (services, servicePackage, declinedServices) =>
    services.length + declinedServices.length
);

export const getCustomerPhone = createSelector(
  getContactInfo,
  ({ phone }) => phone
);

export const getCustomerEmail = createSelector(
  getContactInfo,
  ({ email }) => email
);

export const getPrivacySettings = createSelector(
  getAppointment,
  ({ privacySettings }) => privacySettings
);

export const getEmailPrivacySetting = createSelector(
  getPrivacySettings,
  ({ emailPrivacySetting }) => +emailPrivacySetting[0] > 0
);

export const getTextPrivacySetting = createSelector(
  getPrivacySettings,
  ({ textPrivacySetting }) => +textPrivacySetting[0] > 0
);

export const getReservationStatusSettings = createSelector(
  getAppointment,
  ({ reservationStatusPrivacySettings }) => reservationStatusPrivacySettings
);

export const getEmailReservationStatusSetting = createSelector(
  getReservationStatusSettings,
  ({ emailPrivacySetting }) => +emailPrivacySetting[12] > 0
);

export const getTextReservationStatusSetting = createSelector(
  getReservationStatusSettings,
  ({ textPrivacySetting }) => +textPrivacySetting[12] > 0
);

export const isIDK = createSelector(
  getVehicle,
  ({ trim, transmission, driveType, engineType, engineSize }) => {
    return (
      isEmpty(trim) &&
      isEmpty(transmission) &&
      isEmpty(driveType) &&
      isEmpty(engineType) &&
      isEmpty(engineSize)
    );
  }
);

export { getContactInfo };

export const getConfirmationKey = createSelector(
  getAppointment,
  ({ confirmationKey }) => confirmationKey
);

export const isPastAppointment = createSelector(
  getStartTime,
  getAttendanceStatus,
  (startTime, attendanceStatus) => {
    const curDateClearTime = clearTime(new Date());
    const apptDateClearTime = clearTime(new Date(parse(startTime)));
    return (
      !isEmpty(startTime) &&
      !(
        apptDateClearTime >= curDateClearTime &&
        (attendanceStatus === UNKNOWN ||
          attendanceStatus === NOT_ARRIVED ||
          attendanceStatus === REVIEWED) &&
        attendanceStatus !== PAUSED
      )
    );
  }
);

export const vehicleEditableEnabled = createSelector(
  getVehicle,
  isLoggedIn,
  isPartiallySignedIn,
  getIsQuoteAppt,
  ({ vehicleId }, isLoggedIn, isPartiallySignedIn, isQuoteAppt) => {
    return (!vehicleId || (!isLoggedIn && isPartiallySignedIn)) && !isQuoteAppt;
  }
);

export const isLeadAppt = createSelector(
  getAppointment,
  getQueryParam('lead'),
  ({ lead: leadFromAppt }, leadFromQP) => {
    return isTrue(leadFromAppt) || isTrue(leadFromQP);
  }
);

export const getLifecycleState = createSelector(
  getAppointment,
  ({ lifecycleState }) => lifecycleState
);

export const isNewLeadAppt = createSelector(
  getLifecycleState,
  lifecycleState => lifecycleState === 'NEW_LEAD'
);

export const getActivePromoApptData = createSelector(
  getActivePromotion,
  getTotalPrice,
  isApptInPromoInterval,
  (activePromotion, totalPrice, isApptInPromo) =>
    activePromotion && isApptInPromo
      ? {
          promoOpCode: activePromotion.promoOpCode,
          promoId: activePromotion.promoCode,
          promoDescription: activePromotion.promotionDescription,
          promoDiscountAmount: totalPrice.price - totalPrice.discountedPrice
        }
      : null
);

export const isModifyAppt = createSelector(
  getReservationId,
  reservationId => reservationId && reservationId > 0
);

export const isDmsAppt = createSelector(
  getAppointment,
  ({ isDmsAppt }) => isDmsAppt
);

export const isBookApptInProgress = createSelector(
  getAjaxCallStack,
  stackRequest => {
    if (
      stackRequest &&
      stackRequest.find(
        service =>
          service.getId() === 'confirm' || service.getId() === 'reschedule'
      )
    ) {
      return true;
    }
    return false;
  }
);

export const isQuoteAppt = createSelector(
  getAppointment,
  ({ isQuoteAppt }) => isQuoteAppt
);



// WEBPACK FOOTER //
// ./src/store/reducers/appointment/selectors.js